import { memo, useId, useMemo } from "react";

import type { SldModelRequest, SldWarning, StructuralError } from "../services/sld";
import type { SourceKind } from "../services/sldContract";
import {
  deviceWords,
  kindLabel,
  layoutSld,
  SLD_LAYOUT,
  sourceRatingWords,
  splitPolyline,
  withUnit,
  type LayoutEdge,
  type LayoutLabel,
  type LayoutLabelKind,
  type LayoutNode,
  type LayoutPoint,
} from "../sld/layout";
import { BUS_KIND_LABELS, type SldDraft } from "../sld/sldDraft";

// The single-line diagram as a picture (SLD-a, Master Prompt A26 (g)): one SVG rendered
// from the deterministic layout of sld/layout.ts. The shapes are generic - a circle for a
// source with a mark for its kind (~ grid, G generator, U UPS, PV), a bar for a bus, two
// circles for a transformer, a small square for a device, a dashed line for a cable, a
// circle with M for a motor - not IEC 60617 symbols (GAP-023),
// and the legend says so. Every label sits where the layout placed it: beside its symbol
// or its run, clear of every route, symbol and other label (layout.labels). An element a
// structural error names is outlined in the fail tone; a bus no source energises in the
// warning tone. Elements the layout could not place (a draft's cycle or loose end) sit on
// the "Unconnected" row.
//
// Every colour comes from the design tokens through styles/sld.css; nothing here names a
// colour. The component is memoised and so is its layout: the live preview (SldPreview)
// re-renders on a timer, and a tick that brings the same model, errors and warnings must
// cost nothing.

export const EMPTY_DIAGRAM_HINT =
  "Add a source, a bus and an incomer to see the diagram here.";
export const GENERIC_SHAPES_NOTE = "Generic shapes, not IEC 60617 symbols (GAP-023).";
export const SOURCE_LEGEND = "Source (~ grid, G generator, U UPS, PV)";

// The mark inside a source's circle, one per kind of the contract and each different: a
// grid is not a generator (chief review of the gallery, 30 Sep 2026). Exhaustive:
// SldDiagram.test.tsx holds it to sldContract. A source whose kind is not chosen yet shows
// an empty circle.
export const SOURCE_MARKS: Record<SourceKind, string> = {
  GRID: "~",
  GENERATOR: "G",
  UPS: "U",
  PV: "PV",
};
export const UNCONNECTED_TITLE = "Unconnected";

const { sourceRadius: SOURCE_RADIUS, loadRadius: LOAD_RADIUS } = SLD_LAYOUT.symbol;
const { transformerRadius: TRANSFORMER_RADIUS, deviceSize: DEVICE_SIZE } = SLD_LAYOUT.symbol;
const LABEL_GAP = SLD_LAYOUT.label.gap;
const LINE_HEIGHT = SLD_LAYOUT.label.lineHeight;

export type SldDiagramProps = {
  /** The model as the draft builds it: rows without a code or a target are tolerated. */
  model: SldModelRequest;
  errors?: readonly StructuralError[] | undefined;
  warnings?: readonly SldWarning[] | undefined;
};

/** One shared empty list, so a picture without marks keeps the same props between renders. */
const NONE: readonly never[] = [];

/**
 * The draft as a model the layout can draw while the engineer is still typing. Rows without
 * a code are left out; a device or a cable without a code is left off its branch; a feeder
 * whose target is not chosen yet has no far end, so the layout puts it on the unconnected
 * row. Any field a row lacks - a row not filled yet, a draft restored from an older shape -
 * counts as blank, so the preview never throws: it draws what it can and skips the rest.
 * The diagram never judges the values; the save does.
 */
export function previewModelFromDraft(draft: SldDraft): SldModelRequest {
  type Fields = Record<string, unknown>;
  const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");
  const fields = (value: unknown): Fields =>
    typeof value === "object" && value !== null ? (value as Fields) : {};
  const coded = (value: unknown): Fields[] =>
    Array.isArray(value) ? value.map(fields).filter((row) => text(row.code) !== "") : [];
  const device = (typed: unknown) => {
    const row = fields(typed);
    return text(row.code) === ""
      ? undefined
      : { code: text(row.code), kind: text(row.kind), rating_a: text(row.rating) };
  };
  // The rating of a source under the key of its kind (kVA generator / UPS, AC kW PV).
  const sourceRatingOf = (row: Fields) => {
    const rating = text(row.rating);
    if (rating === "") {
      return {};
    }
    const kind = text(row.kind);
    if (kind === "PV") {
      return { rating_kw: rating };
    }
    return kind === "GENERATOR" || kind === "UPS" ? { rating_kva: rating } : {};
  };
  const cable = (typed: unknown) => {
    const row = fields(typed);
    return text(row.code) === ""
      ? undefined
      : { code: text(row.code), length_m: text(row.length) };
  };
  const source = fields(draft);
  return {
    code: text(source.code),
    name: text(source.name),
    buses: coded(source.buses).map((bus) => ({
      code: text(bus.code),
      name: text(bus.name),
      nominal_voltage_v: text(bus.nominalVoltageV),
      kind: text(bus.kind),
    })),
    sources: coded(source.sources).map((row) => ({
      code: text(row.code),
      kind: text(row.kind),
      voltage_v: text(row.voltageV),
      ...sourceRatingOf(row),
    })),
    transformers: coded(source.transformers).map((transformer) => ({
      code: text(transformer.code),
      from_bus: text(transformer.fromBus),
      to_bus: text(transformer.toBus),
      rating_kva: text(transformer.ratingKva),
      primary_v: text(transformer.primaryV),
      secondary_v: text(transformer.secondaryV),
      device: device(transformer.device),
    })),
    feeders: coded(source.feeders).map((feeder) => ({
      code: text(feeder.code),
      kind: text(feeder.kind),
      from_bus: text(feeder.fromBus),
      to_bus: feeder.targetKind === "bus" ? text(feeder.toBus) : null,
      to_load: feeder.targetKind === "load" ? text(feeder.toLoad) : null,
      device: device(feeder.device),
      cable: cable(feeder.cable),
    })),
    source_incomers: coded(source.incomers).map((incomer) => ({
      code: text(incomer.code),
      source: text(incomer.source),
      to_bus: text(incomer.toBus),
      device: device(incomer.device),
    })),
    loads: coded(source.loads).map((load) => ({
      code: text(load.code),
      kind: text(load.kind),
      rated_kw: text(load.ratedKw),
    })),
    // The kinds are whatever the draft holds ("" before a choice): the layout only reads
    // codes and endpoints, and the symbols look a kind up without trusting it.
  } as SldModelRequest;
}

// -- the picture -----------------------------------------------------------------------------

const pointsAttribute = (points: LayoutPoint[]): string =>
  points.map(([x, y]) => `${x},${y}`).join(" ");

type Marks = Map<string, "error" | "warning">;

function markOf(marks: Marks, code: string): { "data-sld-mark"?: "error" | "warning" } {
  const mark = marks.get(code);
  return mark === undefined ? {} : { "data-sld-mark": mark };
}

/** The placed labels by kind and code: a device code may equal a node code in a broken draft. */
type PlacedLabels = Map<string, LayoutLabel>;

const labelKey = (kind: LayoutLabelKind, id: string): string => `${kind}:${id}`;

/** The words of one label where the layout put them; `dx` / `dy` undo a parent transform. */
function Labels({ label, dx = 0, dy = 0 }: { label: LayoutLabel | undefined; dx?: number; dy?: number }) {
  if (label === undefined) {
    return null;
  }
  const top = label.y - ((label.lines.length - 1) * LINE_HEIGHT) / 2;
  return (
    <>
      {label.lines.map((line, index) => (
        <text
          key={line + index}
          x={label.x + dx}
          y={top + index * LINE_HEIGHT + dy}
          data-sld-label="true"
        >
          {line}
        </text>
      ))}
    </>
  );
}

function SourceSymbol({
  node,
  source,
  label,
  marks,
}: {
  node: LayoutNode;
  source: SldModelRequest["sources"][number] | undefined;
  label: LayoutLabel | undefined;
  marks: Marks;
}) {
  const cx = node.x + node.width / 2;
  const cy = node.y + node.height / 2;
  const mark = kindLabel(SOURCE_MARKS, source?.kind);
  const voltage = withUnit(source?.voltage_v, "V");
  const rating = sourceRatingWords(source);
  return (
    <g
      data-sld-element={node.id}
      data-sld-kind="source"
      aria-label={`Source ${node.id}${voltage ? `, ${voltage}` : ""}${rating ? `, ${rating}` : ""}`}
      {...markOf(marks, node.id)}
    >
      <circle cx={cx} cy={cy} r={SOURCE_RADIUS} />
      {mark !== "" ? (
        <text x={cx} y={cy} data-sld-symbol-text="true" data-sld-source-mark={mark}>
          {mark}
        </text>
      ) : null}
      <Labels label={label} />
    </g>
  );
}

function BusSymbol({
  node,
  bus,
  label,
  marks,
}: {
  node: LayoutNode;
  bus: SldModelRequest["buses"][number] | undefined;
  label: LayoutLabel | undefined;
  marks: Marks;
}) {
  const cy = node.y + node.height / 2;
  const voltage = withUnit(bus?.nominal_voltage_v, "V");
  const kind = kindLabel(BUS_KIND_LABELS, bus?.kind);
  return (
    <g
      data-sld-element={node.id}
      data-sld-kind="bus"
      aria-label={`Bus ${node.id}${voltage ? `, ${voltage}` : ""}${kind ? `, ${kind}` : ""}`}
      {...markOf(marks, node.id)}
    >
      <rect x={node.x} y={node.y} width={node.width} height={node.height} rx={2} />
      <text x={node.x + node.width / 2} y={cy} data-sld-symbol-text="true">
        {node.id}
      </text>
      <Labels label={label} />
    </g>
  );
}

function LoadSymbol({
  node,
  load,
  label,
  marks,
}: {
  node: LayoutNode;
  load: SldModelRequest["loads"][number] | undefined;
  label: LayoutLabel | undefined;
  marks: Marks;
}) {
  const cx = node.x + node.width / 2;
  const cy = node.y + node.height / 2;
  const power = withUnit(load?.rated_kw, "kW");
  const motor = load?.kind === "MOTOR";
  return (
    <g
      data-sld-element={node.id}
      data-sld-kind="load"
      data-sld-load-kind={motor ? "motor" : "other"}
      aria-label={`${motor ? "Motor" : "Load"} ${node.id}${power ? `, ${power}` : ""}`}
      {...markOf(marks, node.id)}
    >
      {motor ? (
        <>
          <circle cx={cx} cy={cy} r={LOAD_RADIUS} />
          <text x={cx} y={cy} data-sld-symbol-text="true">
            M
          </text>
        </>
      ) : (
        <rect
          x={cx - LOAD_RADIUS}
          y={cy - LOAD_RADIUS}
          width={LOAD_RADIUS * 2}
          height={LOAD_RADIUS * 2}
          rx={2}
        />
      )}
      <Labels label={label} />
    </g>
  );
}

type Branches = {
  transformers: Map<string, SldModelRequest["transformers"][number]>;
  feeders: Map<string, SldModelRequest["feeders"][number]>;
  incomers: Map<string, SldModelRequest["source_incomers"][number]>;
};

function BranchSymbol({
  edge,
  branches,
  labels,
  marks,
}: {
  edge: LayoutEdge;
  branches: Branches;
  labels: PlacedLabels;
  marks: Marks;
}) {
  const feeder = branches.feeders.get(edge.id);
  const transformer = branches.transformers.get(edge.id);
  const device =
    feeder?.device ?? branches.incomers.get(edge.id)?.device ?? transformer?.device ?? null;
  const cable = feeder?.cable ?? null;

  // The route: solid up to the cable, dashed for the cable itself; the symbols where the
  // layout put them.
  const [solid, dashed] =
    cable && edge.cableStart !== undefined
      ? splitPolyline(edge.points, edge.cableStart)
      : [edge.points, []];
  const devicePoint = device ? edge.devicePoint : undefined;
  const transformerPoint = transformer ? edge.transformerPoint : undefined;

  const branchName =
    edge.kind === "incomer"
      ? "Incomer"
      : edge.kind === "transformer"
        ? "Transformer"
        : edge.kind === "trunking"
          ? "Busbar trunking"
          : "Feeder";

  return (
    <g
      data-sld-element={edge.id}
      data-sld-kind={edge.kind}
      aria-label={`${branchName} ${edge.id}, ${edge.from} to ${edge.to}`}
      {...markOf(marks, edge.id)}
    >
      <polyline points={pointsAttribute(solid)} />
      {cable && dashed.length > 1 ? (
        <g
          data-sld-element={cable.code}
          data-sld-kind="cable"
          aria-label={["Cable " + cable.code, withUnit(cable.length_m, "m")]
            .filter(Boolean)
            .join(", ")}
          {...markOf(marks, cable.code)}
        >
          <polyline points={pointsAttribute(dashed)} />
          <Labels label={labels.get(labelKey("cable", cable.code))} />
        </g>
      ) : null}
      {transformer && transformerPoint ? (
        <g
          data-sld-kind="transformer"
          data-sld-symbol={edge.id}
          transform={`translate(${transformerPoint[0]} ${transformerPoint[1]})`}
        >
          <circle cx={0} cy={-TRANSFORMER_RADIUS * 0.6} r={TRANSFORMER_RADIUS} />
          <circle cx={0} cy={TRANSFORMER_RADIUS * 0.6} r={TRANSFORMER_RADIUS} />
          <Labels
            label={labels.get(labelKey("transformer", edge.id))}
            dx={-transformerPoint[0]}
            dy={-transformerPoint[1]}
          />
        </g>
      ) : null}
      {device && devicePoint ? (
        <g
          data-sld-element={device.code}
          data-sld-kind="device"
          aria-label={["Device " + device.code, deviceWords(device)].filter(Boolean).join(", ")}
          {...markOf(marks, device.code)}
        >
          <rect
            x={devicePoint[0] - DEVICE_SIZE / 2}
            y={devicePoint[1] - DEVICE_SIZE / 2}
            width={DEVICE_SIZE}
            height={DEVICE_SIZE}
          />
          <Labels label={labels.get(labelKey("device", device.code))} />
        </g>
      ) : null}
    </g>
  );
}

function Legend() {
  return (
    <ul data-sld-legend="true" aria-label="Legend">
      <li>
        <svg width="28" height="16" aria-hidden="true" focusable="false" data-sld-kind="bus">
          <rect x="0" y="4" width="28" height="8" rx="1" />
        </svg>
        Bus
      </li>
      <li>
        <svg width="28" height="16" aria-hidden="true" focusable="false" data-sld-kind="device">
          <polyline points="0,8 8,8" />
          <rect x="8" y="2" width="12" height="12" />
          <polyline points="20,8 28,8" />
        </svg>
        Device
      </li>
      <li>
        <svg width="28" height="16" aria-hidden="true" focusable="false" data-sld-kind="cable">
          <polyline points="0,8 28,8" />
        </svg>
        Cable
      </li>
      <li>
        <svg width="28" height="24" aria-hidden="true" focusable="false" data-sld-kind="transformer">
          <circle cx="14" cy="8" r="6" />
          <circle cx="14" cy="16" r="6" />
        </svg>
        Transformer
      </li>
      <li>
        <svg width="24" height="24" aria-hidden="true" focusable="false" data-sld-kind="source">
          <circle cx="12" cy="12" r="10" />
        </svg>
        {SOURCE_LEGEND}
      </li>
      <li>
        <svg width="24" height="24" aria-hidden="true" focusable="false" data-sld-kind="load">
          <circle cx="12" cy="12" r="10" />
        </svg>
        Motor (M) / load
      </li>
    </ul>
  );
}

export const SldDiagram = memo(function SldDiagram({
  model,
  errors = NONE,
  warnings = NONE,
}: SldDiagramProps) {
  const titleId = useId();
  const descId = useId();
  const layout = useMemo(() => layoutSld(model), [model]);

  if (layout.nodes.length === 0) {
    return (
      <figure data-sld-diagram="true" aria-label="Single-line diagram">
        <p data-sld-empty="true">{EMPTY_DIAGRAM_HINT}</p>
      </figure>
    );
  }

  // An error outranks a warning on the same element.
  const marks: Marks = new Map();
  for (const warning of warnings) {
    if (warning.code === "BUS_NOT_ENERGISED") {
      marks.set(warning.element, "warning");
    }
  }
  for (const error of errors) {
    marks.set(error.element, "error");
  }

  const byCode = <T extends { code: string }>(rows: readonly T[]) =>
    new Map(rows.map((row) => [row.code, row]));
  const buses = byCode(model.buses);
  const sources = byCode(model.sources);
  const loads = byCode(model.loads);
  const branches: Branches = {
    transformers: byCode(model.transformers),
    feeders: byCode(model.feeders),
    incomers: byCode(model.source_incomers),
  };
  const labels: PlacedLabels = new Map(
    layout.labels.map((label) => [labelKey(label.kind, label.id), label]),
  );

  const strandedNodes = layout.nodes.filter((node) => layout.unconnected.includes(node.id));
  const unconnectedTop = strandedNodes.length
    ? Math.min(...strandedNodes.map((node) => node.y))
    : null;

  const description =
    `${model.sources.length} source(s), ${model.buses.length} bus(es), ` +
    `${model.transformers.length} transformer(s), ${model.source_incomers.length} incomer(s), ` +
    `${model.feeders.length} feeder(s), ${model.loads.length} load(s); sources at the top, ` +
    `supply flowing downward` +
    (layout.unconnected.length ? `; ${layout.unconnected.length} element(s) unconnected.` : ".");

  return (
    <figure data-sld-diagram="true" aria-label="Single-line diagram">
      <div data-table-scroll="true">
        <svg
          role="img"
          aria-labelledby={`${titleId} ${descId}`}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          width={layout.width}
          height={layout.height}
          data-sld-width={layout.width}
          data-sld-height={layout.height}
        >
          <title id={titleId}>Single-line diagram {model.code || "(no code)"}</title>
          <desc id={descId}>{description}</desc>
          {layout.edges.map((edge) => (
            <BranchSymbol
              key={edge.id}
              edge={edge}
              branches={branches}
              labels={labels}
              marks={marks}
            />
          ))}
          {layout.nodes.map((node) =>
            node.kind === "source" ? (
              <SourceSymbol
                key={node.id}
                node={node}
                source={sources.get(node.id)}
                label={labels.get(labelKey("source", node.id))}
                marks={marks}
              />
            ) : node.kind === "bus" ? (
              <BusSymbol
                key={node.id}
                node={node}
                bus={buses.get(node.id)}
                label={labels.get(labelKey("bus", node.id))}
                marks={marks}
              />
            ) : (
              <LoadSymbol
                key={node.id}
                node={node}
                load={loads.get(node.id)}
                label={labels.get(labelKey("load", node.id))}
                marks={marks}
              />
            ),
          )}
          {unconnectedTop !== null ? (
            <text
              x={layout.nodes[0] ? Math.min(...layout.nodes.map((node) => node.x)) : 0}
              y={unconnectedTop - LABEL_GAP}
              data-sld-unconnected-title="true"
            >
              {UNCONNECTED_TITLE}
            </text>
          ) : null}
        </svg>
      </div>
      <Legend />
      <p data-sld-note="true">{GENERIC_SHAPES_NOTE}</p>
    </figure>
  );
});

export default SldDiagram;